'use client'

import { useGSAPContext } from '@/hooks'
import { Typo3SectionSpotHeadlessFeature } from '@/types/typo3'
import { Container, Section } from '@/ui/components'
import { ScrollTrigger, gsap } from '@gsap'
import { memo, useCallback, useEffect, useRef } from 'react'
import Card from './Components/Card'

interface Props {
  data: Typo3SectionSpotHeadlessFeature
}

const SectionSliderHome = ({ data }: Props) => {
  const c = data.content
  const sectionRef = useRef<HTMLElement>(null)
  const tlCards = useRef<any>(null)
  const itemsContainerRef = useRef<HTMLDivElement>(null)

  // Calculate the end of the section
  const calculateEnd = useCallback(() => {
    if (!sectionRef.current) {
      return 0
    }
    const section = sectionRef.current.getBoundingClientRect()
    const end = section.bottom - window.innerHeight
    return end
  }, [])

  //Hooks
  useGSAPContext({
    scope: itemsContainerRef,
    callback: () => {
      gsap.registerPlugin(ScrollTrigger)

      if (!sectionRef.current) {
        return
      }

      const cards = gsap.utils.toArray('[data-stack-card]')

      tlCards.current = gsap.timeline({
        paused: true,
        scrollTrigger: {
          trigger: itemsContainerRef.current,
          start: 'top',
          end: () => calculateEnd(),
          scrub: 0.3,
          pin: true,
          pinSpacing: false,
          invalidateOnRefresh: true
        }
      })

      // Set initial scales for stacked cards (each card below is 0.1 smaller)
      cards.forEach((card, i) => {
        if (i > 0) {
          const initialScale = 1 - i * 0.1
          tlCards.current.set(
            card,
            {
              scale: initialScale.toFixed(1)
            },
            '<'
          )
        }
      })

      // Animate each card (except the last one)
      cards.forEach((card, i) => {
        const isLastCard = i === cards.length - 1
        if (isLastCard) {
          return
        }

        tlCards.current.addLabel(`card-${i}`)

        // Animate current card out
        tlCards.current.to(card, {
          scale: 1,
          y: '-100vh',
          ease: 'none'
        })

        // Update scales for all cards below
        // When card[i] animates out, cards below shift up:
        // - card[i+1] becomes scale 1.0 (top card), card[i+2] becomes scale 0.9, card[i+3] becomes scale 0.8, etc.
        for (let j = i + 1; j < cards.length; j++) {
          const newScale = 1 - (j - i - 1) * 0.1
          const scaleValue = Math.max(0.1, newScale).toFixed(1)
          tlCards.current.to(
            cards[j],
            {
              scale: scaleValue
              //rotateX: '0deg'
            },
            '<'
          )
        }

        tlCards.current.set(card, {
          opacity: 0
        })
      })
    },
    deps: [c.items]
  })

  const goToCardByIndex = useCallback(
    (index: number) => {
      tlCards.current.tweenTo(`card-${index}`)
    },
    [tlCards.current]
  )

  useEffect(() => {
    const cards = gsap.utils.toArray('[data-stack-card]')

    if (!cards) {
      return
    }

    const handleMouseMove = (e: MouseEvent) => {
      const x = e.clientX / window.innerWidth
      const y = e.clientY / window.innerHeight
      const xPercent = 1 - (x * 0.6 + 0.2)
      const yPercent = y * 0.15 + 1.5

      cards.forEach((card) => {
        // change the --card-origin css var of the card
        ;(card as HTMLElement).style.setProperty(
          '--card-origin',
          `${xPercent * 100}% ${yPercent * 100}%`
        )
      })
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  if (!c?.items) {
    return null
  }

  return (
    <Section
      data={data}
      className="s-section-slider-home relative overflow-hidden py-0!"
      ref={sectionRef}
    >
      <Container size="md" data-stack-wrapper className="">
        <div
          className="py-3xl relative h-dvh min-h-[50vw]"
          ref={itemsContainerRef}
        >
          {c.items.map((item, index) => (
            <Card
              key={index?.toString()}
              data={item}
              length={c.items.length}
              index={index}
              data-stack-card
              className="origin-(--card-origin)"
              onLinkFocus={(index: number) => goToCardByIndex(index)}
              style={{
                zIndex: c.items.length - index,
                '--card-origin': `50% 150%`
              }}
            />
          ))}
        </div>
        {c.items.map((item, index) => (
          <div key={index?.toString()} className="h-screen" />
        ))}
      </Container>
    </Section>
  )
}

SectionSliderHome.displayName = 'SectionSliderHome'

export default memo(SectionSliderHome)
